import { Button, Icon, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { Link } from 'react-router-dom';

import { useAppliedDraftsByQuote } from '../hooks/requestDraft/useAppliedDraftsByQuote';

type SegmentWithSource = { details_source?: string | null };

/**
 * Marks a flight path whose details came from a booking-confirmation email instead of a person. Rendered wherever
 * flights are shown, not only on the quote variant: the apply writes to the QUOTE's flight path (`quote_id`), and a
 * quote whose legs are not attached to a variant would otherwise show AI-entered flights with nothing saying so.
 */
export const AiFilledFlightsNote = ({
  flightSegments,
  quoteId,
}: {
  flightSegments: SegmentWithSource[] | undefined;
  quoteId?: number;
}) => {
  const bg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const accentColor = useColorModeValue('purple.600', 'purple.300');
  const bodyColor = useColorModeValue('gray.600', 'gray.400');
  const hasAiFilledSegment = !!flightSegments?.some((segment) => segment.details_source === 'Ai');
  const appliedDrafts = useAppliedDraftsByQuote(quoteId, hasAiFilledSegment);
  if (!hasAiFilledSegment) return null;

  const sourceDraft = appliedDrafts?.[0];
  const showSourceLink = !!sourceDraft;

  return (
    <Stack direction="row" align="center" spacing={2} py={1.5} px={4} bgColor={bg} borderTopWidth={1}>
      <Icon as={HiOutlineSparkles} boxSize={4} color={accentColor} />
      <Text textStyle="bodySmallRegular" fontWeight="medium" color={accentColor} whiteSpace="nowrap">
        Filled by AI
      </Text>
      <Text textStyle="bodySmallRegular" color={bodyColor} noOfLines={1}>
        Flight details entered from a booking confirmation email.
      </Text>
      <Show if={showSourceLink}>
        <Button
          as={Link}
          to={`/ai-inbox?draft=${sourceDraft?.id}`}
          size="xs"
          variant="outline"
          flexShrink={0}
          ml="auto"
        >
          View source email
        </Button>
      </Show>
    </Stack>
  );
};
